import { Renderer2, Inject, Output } from '@angular/core';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
  EventEmitter,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { JaakrecogLivenessLibService } from '../jaakrecog-liveness-lib.service';
import { CaptureOptions } from '../interfaces/video-capture-options';
import { Builder } from 'builder-pattern';
import { ErrorResponse } from '../models/error';
@Component({
  selector: 'ngx-capture-video',
  templateUrl: './capture-video.component.html',
  styleUrls: ['./capture-video.component.scss'],
})
export class CaptureVideoComponent implements OnInit {
  @ViewChild('video') video!: ElementRef;
  @Input() stream!: MediaStream;
  @Input() token: string = '';
  @Output() getBestFrame = new EventEmitter();
  stepper = new BehaviorSubject<string>('step1');
  showAnimation = true;
  showLoading = true;
  faceExists = new BehaviorSubject<boolean>(false);
  message!: string;
  trackingFn: any;
  mediaRecorder: any;
  recordedBlobs: any;
  stepperObservable!: Subscription;
  @HostListener('window:resize', ['$event'])
  private onResize() {
    this.resizeEllipsis();
  }
  constructor(
    private renderer: Renderer2,
    private livenessSrv: JaakrecogLivenessLibService,
    public dialogRef: MatDialogRef<CaptureVideoComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CaptureOptions
  ) {
    this.token = data.token;
  }

  async ngOnInit(): Promise<void> {
    const dialog = document.getElementsByTagName('mat-dialog-container')[0];
    this.renderer.setStyle(dialog, 'overflow', 'inherit');
    this.renderer.setStyle(dialog, 'padding', 'inherit');
    this.initStepper();
    await this.initComponent();
    setTimeout(() => {
      this.resizeEllipsis();
    }, 500);
  }
  async initComponent(): Promise<void> {
    this.showLoading = true;
    this.initVideoStreaming();
  }
  initVideoStreaming(): void {
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(async (stream) => {
        this.showLoading = false;
        this.stream = stream;
        setTimeout(() => {
          this.video.nativeElement.srcObject = new MediaStream(stream);
          this.video.nativeElement.play();
        }, 100);
      })
      .catch(async (e) => {
        const error = Builder(ErrorResponse)
          .message('Permissions was denied')
          .type('permissions')
          .build();
        this.getBestFrame.emit(error);
        this.dialogRef.close(error);
      });
  }
  initStepper(): void {
    this.stepperObservable = this.stepper
      .asObservable()
      .subscribe(async (value) => {
        if (value === 'step2') {
          this.resizeEllipsis();
          this.step2();
        }
        if (value === 'step3') {
          this.resizeEllipsis();
          this.step3();
        }
      });
  }
  async start(): Promise<void> {
    this.stepper.next('step2');
  }
  async step2(): Promise<void> {
    this.message = 'Analisis facial';
    setTimeout(() => {
      this.goToStep3();
    }, 3000);
  }

  goToStep2(): void {
    this.stepper.next('step2');
  }

  step3(): void {
    this.stepperObservable.unsubscribe();
    this.message = 'Grabando';
    this.startRecording();
    setTimeout(async () => {
      this.mediaRecorder.pause();
      this.mediaRecorder.stop();
      const blob = this.getBlob();
      const video = await this.getBase64(blob);
      this.stopStreaming();
      setTimeout(async () => {
        await this.verifyVideo(video);
      }, 100);
    }, 5000);
  }

  goToStep3(): void {
    this.stepper.next('step3');
  }
  stopStreaming(): void {
    const stream = this.video.nativeElement.srcObject;
    const tracks = stream.getTracks();

    tracks.forEach((track: any) => {
      track.stop();
    });
    this.video.nativeElement.srcObject = null;
  }
  startRecording(): void {
    let options = { mimeType: 'video/webm; codecs=opus,vp8' };
    if (MediaRecorder.isTypeSupported) {
      options = { mimeType: 'video/webm;codecs=vp9' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        console.error(`${options.mimeType} is not Supported`);
        options = { mimeType: 'video/webm;codecs=vp8' };
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          console.error(`${options.mimeType} is not Supported`);
          options = { mimeType: 'video/webm' };
          if (!MediaRecorder.isTypeSupported(options.mimeType)) {
            console.error(`${options.mimeType} is not Supported`);
            options = { mimeType: '' };
          }
        }
      }
    } else {
      options = { mimeType: '' };
    }
    this.recordedBlobs = [];
    try {
      this.mediaRecorder = new MediaRecorder(this.stream, options);
    } catch (e) {
      console.log(e);
      const error = Builder(ErrorResponse)
        .type('compatibility')
        .message('This browser is not supported');
      this.getBestFrame.emit(error);
      this.dialogRef.close(error);
    }
    this.mediaRecorder.ondataavailable = (event: any) => {
      if (event.data && event.data.size > 0) {
        this.recordedBlobs.push(event.data);
      }
    };
    this.mediaRecorder.start(100);
  }

  getBlob(): Blob {
    const blob = new Blob(this.recordedBlobs, { type: 'video/webm' });
    return blob;
  }

  getBase64(file: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = (error) => reject(error);
    });
  }

  async verifyVideo(video: string): Promise<any> {
    try {
      this.showLoading = true;
      const res = await this.livenessSrv
        .verifyBestFrame([video], this.token)
        .pipe(first())
        .toPromise();
      if (res?.status && res?.evaluation > 90) {
        this.getBestFrame.emit(res);
        this.dialogRef.close(res);
      } else {
        const error = Builder(ErrorResponse)
          .type('uknow')
          .message(res ? res.message : 'Something was wrong')
          .build();
        this.getBestFrame.emit(error);
        this.dialogRef.close(error);
      }
    } catch (e: any) {
      console.log(e);
      if (e.error?.errors.length) {
        const error = Builder(ErrorResponse)
          .message(
            e.error.errors[0] ? e.error.errors[0] : 'Something was wrong'
          )
          .type(e.status === 401 ? 'unauthorized' : 'server error')
          .build();
        this.getBestFrame.emit(error);
        this.dialogRef.close(error);
      } else {
        this.getBestFrame.emit(e);
        this.dialogRef.close(e);
      }
    }
  }

  resizeEllipsis(): void {
    if (window.innerWidth < 768) {
      setTimeout(() => {
        const ellipsis = document.getElementById('ellipse');
        const svg = document.getElementsByTagName('svg')[0];
        const svgWidth = svg.clientWidth;
        const svgHeight = svg.clientHeight;
        const magicNumberX = 0.6956521739130435;
        const magicNumberY = 0.9352708058124174;
        this.renderer.setAttribute(ellipsis, 'cx', (svgWidth / 2).toString());
        this.renderer.setAttribute(ellipsis, 'cy', (svgHeight / 2).toString());
        this.renderer.setAttribute(
          ellipsis,
          'rx',
          ((svgWidth / 2) * magicNumberX).toString()
        );
        this.renderer.setAttribute(
          ellipsis,
          'ry',
          ((svgHeight / 2) * magicNumberY).toString()
        );
        if (ellipsis) ellipsis.style.display = 'block';
      }, 100);
    } else if (window.innerHeight < 840 && window.innerWidth > 900) {
      setTimeout(() => {
        const ellipsis = document.getElementById('ellipse');
        const svg = document.getElementsByTagName('svg')[0];
        const svgWidth = svg.clientWidth;
        const svgHeight = svg.clientHeight;
        const magicNumberX = 0.6956521739130435;
        const magicNumberY = 0.9352708058124174;
        this.renderer.setAttribute(ellipsis, 'cx', (svgWidth / 2).toString());
        this.renderer.setAttribute(ellipsis, 'cy', (svgHeight / 2).toString());
        this.renderer.setAttribute(
          ellipsis,
          'rx',
          (((svgWidth * 0.27) / 2) * magicNumberX).toString()
        );
        this.renderer.setAttribute(
          ellipsis,
          'ry',
          ((svgHeight / 2) * magicNumberY).toString()
        );
        if (ellipsis) ellipsis.style.display = 'block';
      }, 100);
    } else {
      setTimeout(() => {
        const ellipsis = document.getElementById('ellipse');
        this.renderer.setAttribute(ellipsis, 'cx', (300).toString());
        this.renderer.setAttribute(ellipsis, 'cy', (300).toString());
        this.renderer.setAttribute(ellipsis, 'rx', (176).toString());
        this.renderer.setAttribute(ellipsis, 'ry', (265).toString());
      }, 50);
    }
  }
}
